import { api, apiErrorMessage, useZodForm, zodCents } from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    DateTime,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Field,
    FormSheet,
    Input,
    KeyValueList,
    Money,
    MoneyInput,
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    Skeleton,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Textarea,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { z } from 'zod';

import type {
    Refund,
    StaffCharge,
    StaffChargeRow,
    StaffRefund,
} from '@/api/types';
import { ReferenceId } from '@/components/charge';
import { applyMoneyErrors } from '@/lib/money-errors';
import { P } from '@/lib/permissions';
import { useList, useOne, useWrite } from '@/lib/queries';

const CHARGE_ID = /^chg_[0-9A-Z]{26}$/;

/** bm-147 § 9: a refund's source in words, the ticket's own (item 8 of BM-147). */
export const REFUND_SOURCE_WORDS: Record<Refund['source'], string> = {
    api: 'Merchant API',
    dashboard: 'Merchant dashboard',
    staff: 'Staff',
    system: 'System',
};

const schema = z.object({
    charge_id: z.string().regex(CHARGE_ID, 'A charge id looks like chg_…'),
    // BM-46 §2.7: integer cents from the money input (never through floating point)
    amount: zodCents({ min: 1 }),
    reason: z.string().min(3, 'Give a short reason'),
});

// Charge::isApproved(): a capture or a sale took the funds; a refund keeps the charge in this set
const CAPTURED = ['approved', 'partially_refunded', 'refunded'];

/**
 * bm-111 § 11: whether a charge offers the Refund action. It mirrors the refund service's guards (captured, something
 * left once pending-approval refunds count) so the sheet never opens on a refusal; the service still decides.
 */
export function canRefund(
    charge: Pick<StaffChargeRow, 'state' | 'refundable_cents'>,
    can: (permission: string) => boolean,
): boolean {
    return (
        can(P.refundsIssue) &&
        CAPTURED.includes(charge.state) &&
        charge.refundable_cents > 0
    );
}

/**
 * bm-111 QA-3: the refunds already requested for a bound charge (`GET /staff/refunds?charge_id[]=`, BM-112 D-36),
 * newest first; a refund recorded from the sheet joins it once the list is read again.
 */
function ChargeRefunds({ chargeId }: { chargeId: string }) {
    // bm-146 § 10 (D-14): the staff row carries the refund's processor_txn_id, shown as its Reference ID
    const refunds = useList<StaffRefund>('/staff/refunds', {
        charge_id: [chargeId],
        sort: '-created_at',
        per_page: 100,
    });
    const rows = refunds.data?.data.items ?? [];
    return (
        <section className="flex flex-col gap-2" data-testid="charge-refunds">
            <h3 className="text-fg-1 m-0 text-sm font-semibold">Refunds</h3>
            {refunds.error ? (
                <p role="alert" className="text-destructive m-0 text-sm">
                    {apiErrorMessage(refunds.error)}
                </p>
            ) : refunds.isPending ? (
                <Skeleton className="h-16 w-full" />
            ) : rows.length === 0 ? (
                <p className="text-muted-foreground m-0 text-sm">
                    No refund was requested for this charge yet.
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Amount</TableHead>
                                <TableHead>State</TableHead>
                                <TableHead>Reason</TableHead>
                                <TableHead>Requested by</TableHead>
                                <TableHead>Created</TableHead>
                                <TableHead>Issued</TableHead>
                                <TableHead>Reference ID</TableHead>
                                <TableHead>Failure</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow
                                    key={r.id}
                                    data-testid="charge-refund"
                                >
                                    <TableCell className="tabular-nums">
                                        <Money
                                            cents={r.amount_cents}
                                            currency={r.currency}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <StatusBadge
                                            status={r.state}
                                            domain="refund"
                                        />
                                    </TableCell>
                                    <TableCell className="break-words">
                                        {r.reason ?? '—'}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs break-all">
                                        {r.initiated_by}
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap">
                                        <DateTime value={r.created_at} />
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap">
                                        <DateTime
                                            value={r.issued_at}
                                            empty="—"
                                        />
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap">
                                        <ReferenceId
                                            value={r.processor_txn_id}
                                        />
                                    </TableCell>
                                    <TableCell className="text-destructive text-xs">
                                        {r.failure_reason ?? '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </section>
    );
}

/**
 * bm-111 QA-3: the bound charge at a glance: the charge amount, the order's base and tax (the order's figures, which a
 * trial hold's amount is not), what was refunded, what is in flight and what is left. Refunded is the issued sum and
 * Remaining the API's refundable_cents (amount minus pending_approval, sending, unknown and issued refunds), so In
 * flight is their gap and charge = refunded + in flight + remaining always reads true (display only).
 */
function ChargeRefundSummary({ charge: c }: { charge: StaffCharge }) {
    const money = (cents: number | null | undefined) =>
        cents == null ? '—' : <Money cents={cents} currency={c.currency} />;
    // ponytail: the identity holds for a captured charge (bm-146 § 10: the refund view also opens on held and declined
    // ones, whose refundable_cents 0 is no money in flight, so theirs reads 0). Upgrade: an API in-flight sum.
    const inFlight = CAPTURED.includes(c.state)
        ? Math.max(0, c.amount_cents - c.refunded_cents - c.refundable_cents)
        : 0;
    return (
        <Card data-testid="refund-summary">
            <CardContent className="pt-4">
                <KeyValueList
                    items={[
                        { label: 'Charge', value: c.id, mono: true },
                        {
                            label: 'Charge amount',
                            value: money(c.amount_cents),
                            num: true,
                        },
                        {
                            label: 'Order base',
                            value: money(c.order?.subtotal_cents),
                            num: true,
                        },
                        {
                            label: 'Order tax',
                            value: money(c.order?.tax_cents),
                            num: true,
                        },
                        {
                            label: 'Refunded',
                            value: money(c.refunded_cents),
                            num: true,
                        },
                        {
                            label: 'In flight',
                            value: money(inFlight),
                            num: true,
                        },
                        {
                            label: 'Remaining',
                            value: money(c.refundable_cents),
                            num: true,
                        },
                    ]}
                />
            </CardContent>
        </Card>
    );
}

/**
 * bm-146 § 10: the refund view of a charge canRefund does not offer the Issue a refund action on (nothing left,
 * not captured, or no payments.refunds.issue): the same summary and refund list as the bound sheet, with no form.
 */
export function RefundView({
    charge,
    onClose,
}: {
    charge: StaffCharge;
    onClose: () => void;
}) {
    return (
        <Sheet open onOpenChange={(o) => (o ? null : onClose())}>
            <SheetContent
                side="right"
                className="w-full overflow-y-auto sm:max-w-3xl"
                data-testid="refund-view"
            >
                <SheetHeader>
                    <SheetTitle>Refunds</SheetTitle>
                    <SheetDescription>
                        The refunds requested for this charge.
                    </SheetDescription>
                </SheetHeader>
                <div className="flex flex-col gap-4 px-4 pb-4">
                    <ChargeRefundSummary charge={charge} />
                    <ChargeRefunds chargeId={charge.id} />
                </div>
            </SheetContent>
        </Sheet>
    );
}

/**
 * The "Issue a refund" sheet and its recorded-outcome dialog (BM-84 rule E.9), opened from the refunds page (charge id
 * typed, or prefilled from `?charge=`) and, bound to one charge, from the charge list row and the charge page
 * (bm-111 § 11): bound, the amount starts at what is left to refund. Bound, the sheet shows the charge's summary and
 * its refunds above the form and stays open after a submit, the new refund joining the table (QA-3). Mounted while
 * open; `onClose` runs once the sheet or the outcome is dismissed.
 */
export function RefundSheet({
    bound,
    initialChargeId = '',
    onClose,
}: {
    bound?: Pick<StaffChargeRow, 'id' | 'refundable_cents'>;
    initialChargeId?: string;
    onClose: () => void;
}) {
    const [error, setError] = useState<unknown>(null);
    const [outcome, setOutcome] = useState<Refund | null>(null);
    const form = useZodForm(schema, {
        defaultValues: {
            charge_id: bound?.id ?? initialChargeId,
            amount: (bound?.refundable_cents ?? null) as unknown as number,
            reason: '',
        },
    });
    const chargeId = form.watch('charge_id');
    const charge = useOne<StaffCharge>(
        CHARGE_ID.test(chargeId) ? `/staff/charges/${chargeId}` : null,
    );
    const issue = useWrite(
        async (body: {
            charge_id: string;
            amount_cents: number;
            reason: string;
        }) =>
            (
                await api().post<Refund>('/staff/refunds', body, {
                    idempotencyKey: crypto.randomUUID(),
                })
            ).data,
        // bm-147 § 3: the order page opens this sheet too, and its Refundable, refunds and Refund gate are the order's
        [
            '/staff/refunds',
            '/staff/approvals',
            '/staff/charges',
            '/staff/orders',
        ],
    );
    const remaining =
        charge.data?.data.refundable_cents ?? bound?.refundable_cents ?? null;
    // QA-3: bound, the outcome is a line above the form and the sheet stays open
    const [recorded, setRecorded] = useState<Refund | null>(null);
    const onRecorded = (refund: Refund) => {
        if (!bound) return setOutcome(refund);
        setRecorded(refund);
        form.reset({
            charge_id: bound.id,
            amount: null as unknown as number,
            reason: '',
        });
    };
    return (
        <>
            <Dialog
                open={outcome !== null}
                onOpenChange={(o) => (o ? null : onClose())}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Refund recorded</DialogTitle>
                    </DialogHeader>
                    {outcome ? (
                        <div
                            className="space-y-3 text-sm"
                            data-testid="refund-outcome"
                        >
                            <p>
                                <StatusBadge
                                    status={outcome.state}
                                    domain="refund"
                                />{' '}
                                <Money
                                    cents={outcome.amount_cents}
                                    currency={outcome.currency}
                                />{' '}
                                on{' '}
                                <span className="font-mono text-xs">
                                    {outcome.charge_id}
                                </span>
                            </p>
                            {outcome.state === 'pending_approval' ? (
                                <p className="text-muted-foreground">
                                    It is above the limit: a second person has
                                    to grant it in the approvals queue.
                                </p>
                            ) : null}
                            <Button onClick={onClose}>Done</Button>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
            <FormSheet
                open={outcome === null}
                onOpenChange={(o) => (o ? null : onClose())}
                size={bound ? 'lg' : 'sm'}
                title="Issue a refund"
                submitLabel="Issue refund"
                busy={issue.isPending}
                submitDisabled={bound !== undefined && remaining === 0}
                dirty={form.formState.isDirty}
                error={error}
                data-testid="refund-form"
                onSubmit={form.handleSubmit((values) => {
                    setError(null);
                    setRecorded(null);
                    issue.mutate(
                        {
                            charge_id: values.charge_id,
                            amount_cents: values.amount,
                            reason: values.reason,
                        },
                        {
                            onSuccess: onRecorded,
                            onError: (e) => {
                                if (!applyMoneyErrors(form, e)) setError(e);
                            },
                        },
                    );
                })}
            >
                {bound ? (
                    <>
                        {charge.data ? (
                            <ChargeRefundSummary charge={charge.data.data} />
                        ) : (
                            <Skeleton className="h-40 w-full" />
                        )}
                        <ChargeRefunds chargeId={bound.id} />
                        {recorded ? (
                            <p
                                role="status"
                                className="m-0 text-sm"
                                data-testid="refund-recorded"
                            >
                                Refund recorded:{' '}
                                <Money
                                    cents={recorded.amount_cents}
                                    currency={recorded.currency}
                                />{' '}
                                <StatusBadge
                                    status={recorded.state}
                                    domain="refund"
                                />
                                {recorded.state === 'pending_approval'
                                    ? ' It is above the limit: a second person has to grant it in the approvals queue.'
                                    : null}
                            </p>
                        ) : null}
                        {remaining === 0 ? (
                            <p className="text-muted-foreground m-0 text-sm">
                                Nothing is left to refund on this charge.
                            </p>
                        ) : null}
                    </>
                ) : (
                    <Field
                        id="charge_id"
                        label="Charge"
                        error={form.formState.errors.charge_id?.message}
                    >
                        <Input
                            id="charge_id"
                            placeholder="chg_…"
                            {...form.register('charge_id')}
                        />
                    </Field>
                )}
                {charge.data && !bound ? (
                    <p className="text-muted-foreground text-xs">
                        <Money cents={charge.data.data.amount_cents} /> charged,{' '}
                        <Money cents={remaining} /> refundable, merchant{' '}
                        <span className="font-mono">
                            {charge.data.data.merchant_id}
                        </span>
                        .
                    </p>
                ) : null}
                <Field
                    id="amount"
                    label="Amount (USD)"
                    error={form.formState.errors.amount?.message}
                >
                    <MoneyInput
                        id="amount"
                        cents={form.watch('amount')}
                        max={remaining ?? undefined}
                        invalid={Boolean(form.formState.errors.amount)}
                        onChange={(cents) =>
                            form.setValue('amount', cents as number, {
                                shouldValidate: form.formState.isSubmitted,
                            })
                        }
                    />
                </Field>
                <Field
                    id="reason"
                    label="Reason"
                    error={form.formState.errors.reason?.message}
                >
                    {/* bm-146 § 11: a new element per recorded refund. The kit's register adopts text already in the
                        input whenever its ref attaches, and the reset above leaves the old reason in this textarea,
                        so the cleared form read dirty again and closing it asked "Leave without saving?". */}
                    <Textarea
                        key={recorded?.id}
                        id="reason"
                        rows={2}
                        {...form.register('reason')}
                    />
                </Field>
            </FormSheet>
        </>
    );
}
